import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TASK_ASSIGNEES, TASK_PRIORITIES, TASK_STATUSES, TASK_WORKSTREAMS, RELATION_FIELDS, ukToday } from "@/data/taskOptions";
import { Star, Sparkles } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useUser } from "@/context/UserContext";
import { launchBadge, WORKSTREAM_COLOR } from "@/data/launchOptions";
import { suggestMilestones, tokenize } from "@/data/milestoneSuggest";

const NONE = "__none__";

const fmtDue = (iso) => {
  if (!iso) return "No date";
  try { return "Due " + new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return iso; }
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-[#5A6B8C]">{label}</Label>
    {children}
  </div>
);

const Sel = ({ testid, value, onChange, options, placeholder }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="border-[#E4E9F3] bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

export const TaskModal = ({ open, onOpenChange, initial, relations, onSubmit }) => {
  const [v, setV] = useState({});
  useEffect(() => { if (open) setV(initial || { assigned_to: "Steven", priority: "Medium", status: "To do", workstream: "Operations", task_date: ukToday() }); }, [open, initial]);

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const { canWrite } = useUser();

  const relName = (rf, id) => {
    const src = rf.key === "related_chapter_id" ? (relations.chapters || []) : (relations[rf.resource] || []);
    const r = src.find((x) => x.id === id);
    return r ? (r[rf.nameKey] || r.name || r.title || "") : "";
  };

  // Debounced context so suggestions recompute only after the user pauses typing.
  // Matching is fully local (no API / LLM calls) over already-loaded milestones.
  const [dctx, setDctx] = useState(null);
  useEffect(() => {
    if (!open) { setDctx(null); return; }
    const id = setTimeout(() => setDctx({
      title: v.title || "", description: v.description || "", notes: v.notes || "", workstream: v.workstream || "",
      related: RELATION_FIELDS.filter((rf) => rf.key !== "related_milestone_id" && v[rf.key] && v[rf.key] !== NONE)
        .map((rf) => ({ key: rf.key, name: relName(rf, v[rf.key]) })),
    }), 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, v.title, v.description, v.notes, v.workstream, v.related_module_id, v.related_chapter_id, v.related_campaign_id, v.related_investor_id, v.related_innovation_id]);

  const milestoneCandidates = useMemo(() => relations["launch-milestones"] || [], [relations]);
  const suggestions = useMemo(() => (dctx ? suggestMilestones(milestoneCandidates, dctx) : []), [dctx, milestoneCandidates]);
  const enoughContext = !!dctx && (tokenize([dctx.title, dctx.description, dctx.notes].join(" ")).length >= 2 || (dctx.related && dctx.related.length > 0));

  // chapters filtered by selected module (child filtering)
  const chapterOptions = useMemo(() => {
    const all = relations.chapters || [];
    const list = v.related_module_id ? all.filter((c) => c.module_id === v.related_module_id) : all;
    return list.map((c) => ({ value: c.id, label: c.name }));
  }, [relations.chapters, v.related_module_id]);

  const relOptions = (rf) => {
    if (rf.key === "related_chapter_id") return chapterOptions;
    const src = relations[rf.resource] || [];
    return src.map((r) => ({ value: r.id, label: r[rf.nameKey] || r.name || r.title }));
  };

  const submit = async () => {
    if (!v.title || !v.title.trim()) { toast.error("Task title is required"); return; }
    const payload = { ...v };
    RELATION_FIELDS.forEach((rf) => { if (payload[rf.key] === NONE) payload[rf.key] = ""; });
    if (payload.estimated_minutes === "") payload.estimated_minutes = null;
    if (payload.actual_minutes === "") payload.actual_minutes = null;
    await onSubmit(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-modal" className="max-h-[92vh] max-w-2xl overflow-y-auto border-[#E4E9F3] bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#14245F]">{v.id ? "Edit Task" : "Add Task"}</DialogTitle>
          <DialogDescription className="text-[#5A6B8C]">Daily tasks are separate from priorities, blockers and milestones.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Task title"><Input data-testid="task-title-input" value={v.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Finalise launch checklist" className="border-[#E4E9F3]" /></Field></div>
          <div className="sm:col-span-2"><Field label="Description"><Textarea data-testid="task-desc-input" value={v.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="What needs doing?" className="border-[#E4E9F3]" /></Field></div>

          <Field label="Assigned to"><Sel testid="task-assigned-select" value={v.assigned_to} onChange={(x) => set("assigned_to", x)} options={TASK_ASSIGNEES} placeholder="Assignee" /></Field>
          <Field label="Workstream"><Sel testid="task-workstream-select" value={v.workstream} onChange={(x) => set("workstream", x)} options={TASK_WORKSTREAMS} placeholder="Workstream" /></Field>
          <Field label="Priority"><Sel testid="task-priority-select" value={v.priority} onChange={(x) => set("priority", x)} options={TASK_PRIORITIES} placeholder="Priority" /></Field>
          <Field label="Status"><Sel testid="task-status-select" value={v.status} onChange={(x) => set("status", x)} options={TASK_STATUSES} placeholder="Status" /></Field>

          <Field label="Task date (scheduled)"><Input type="date" data-testid="task-date-input" value={v.task_date || ""} onChange={(e) => set("task_date", e.target.value)} className="border-[#E4E9F3]" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Due date"><Input type="date" data-testid="task-due-date-input" value={v.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="border-[#E4E9F3]" /></Field>
            <Field label="Due time"><Input type="time" data-testid="task-due-time-input" value={v.due_time || ""} onChange={(e) => set("due_time", e.target.value)} className="border-[#E4E9F3]" /></Field>
          </div>

          <Field label="Estimated time (mins)"><Input type="number" min="0" data-testid="task-est-input" value={v.estimated_minutes ?? ""} onChange={(e) => set("estimated_minutes", e.target.value)} className="border-[#E4E9F3]" /></Field>
          <Field label="Actual time (mins)"><Input type="number" min="0" data-testid="task-actual-input" value={v.actual_minutes ?? ""} onChange={(e) => set("actual_minutes", e.target.value)} className="border-[#E4E9F3]" /></Field>

          <div className="sm:col-span-2 mt-1 border-t border-[#EEF2FB] pt-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#9DAAC6]">Related records (optional)</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {RELATION_FIELDS.map((rf) => (
                <Field key={rf.key} label={rf.label}>
                  <Sel testid={`task-${rf.key}-select`} value={v[rf.key] || NONE} onChange={(x) => set(rf.key, x === NONE ? "" : x)} options={[{ value: NONE, label: "None" }, ...relOptions(rf)]} placeholder="None" />
                  {rf.key === "related_milestone_id" && enoughContext && (
                    <div className="mt-2" data-testid="milestone-suggestions">
                      <p className="mb-1.5 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#9DAAC6]"><Sparkles size={11} className="text-[#E0A82E]" /> Suggested milestones</p>
                      {suggestions.length === 0 ? (
                        <p data-testid="milestone-suggestions-empty" className="rounded-lg bg-[#F7F9FE] px-2.5 py-2 text-xs text-[#5A6B8C]">No strong milestone match found</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {suggestions.map((m) => {
                            const selected = v.related_milestone_id === m.id;
                            return (
                              <li key={m.id} data-testid={`milestone-suggestion-${m.id}`} className="rounded-xl border border-[#E4E9F3] bg-white px-2.5 py-2">
                                <div className="flex items-start gap-1.5">
                                  <Star size={13} className="mt-0.5 shrink-0 text-[#E0A82E]" fill="#E0A82E" />
                                  <p className="line-clamp-2 flex-1 text-xs font-semibold leading-snug text-[#14245F]">{m.title}</p>
                                  {canWrite && (
                                    <Button size="sm" variant="outline" disabled={selected} data-testid={`milestone-suggestion-select-${m.id}`} onClick={() => set("related_milestone_id", m.id)} className="h-6 shrink-0 rounded-full border-[#E4E9F3] px-2.5 text-[11px]">
                                      {selected ? "Selected" : "Select"}
                                    </Button>
                                  )}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5 pl-[19px] text-[10px] text-[#5A6B8C]">
                                  <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: WORKSTREAM_COLOR[m.workstream] || "#94A3B8" }} />{m.workstream || "—"}</span>
                                  <span>· {fmtDue(m.due_date)}</span>
                                  <StatusBadge status={launchBadge(m.status)} label={m.status} />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </Field>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2"><Field label="Notes"><Textarea data-testid="task-notes-input" value={v.notes || ""} onChange={(e) => set("notes", e.target.value)} className="border-[#E4E9F3]" /></Field></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E4E9F3]">Cancel</Button>
          <Button data-testid="task-modal-submit" onClick={submit} className="bg-[#1D4ED8] text-white hover:bg-[#1A45C0]">{v.id ? "Save Changes" : "Add Task"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
